import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { withLocalize } from 'react-localize-redux';
import { getReadOnly } from '../selectors/permissions';
import { getNavigationSections } from '../selectors/navigation';
import EmptyState from '../components/widgets/EmptyState';
import Button from '../components/atoms/Button';
import TextField from '../components/molecules/TextField';
import GeneralPopup, { PopupTypes } from '../components/organisms/GeneralPopup';
import PermissionsWrapper from '../containers/PermissionsWrapper';
import { PageHeader, PageWrapper } from '../components/partials';
import toaster from '../utils/toaster';
import GoalCard from './components/GoalCard';
import GoalShelf from './components/GoalShelf';
import { deleteGoal, fetchCostCurrency, fetchGoals, invalidateGoalsListCache } from './service';
import {
    clearShelfDeepLink,
    filterGoalsByName,
    parseShelfDeepLink,
    sortGoalsByStatus,
    trackGoalsEvent,
} from './utils';
import { FAQ_LINK, SAVE_ACTIONS, SEARCH_VISIBILITY_THRESHOLD, SHELF_MODES, TRANSLATION_KEY } from './constants';
import css from './style.css';

const TOAST_OPTIONS = { autoClose: 3000 };

const SAVE_ACTION_TOASTS = {
    [SAVE_ACTIONS.CREATED]: 'GOAL_CREATED_TOAST',
    [SAVE_ACTIONS.SAVED]: 'GOAL_SAVED_TOAST',
    [SAVE_ACTIONS.DELETED]: 'GOAL_DELETED_TOAST',
};

const SKELETON_CARDS_COUNT = 2;

const GoalCardSkeleton = () => (
    <div className={css.skeletonCard}>
        <div className={css.skeletonTitle} />
        <div className={css.skeletonLine} />
        <div className={css.skeletonLine} />
    </div>
);

const Goals = ({ translate = () => {} }) => {
    const [goalsLoading, setGoalsLoading] = useState(true);
    const [currencyLoading, setCurrencyLoading] = useState(true);
    const [goals, setGoals] = useState([]);
    const [currencySymbol, setCurrencySymbol] = useState('');
    const [loadError, setLoadError] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [shelfState, setShelfState] = useState(null);
    const [goalToDelete, setGoalToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const readOnly = useSelector(getReadOnly);
    const permissionsLoaded = !!useSelector(getNavigationSections);
    const readOnlyRef = useRef(readOnly);
    readOnlyRef.current = readOnly;
    const permissionsLoadedRef = useRef(permissionsLoaded);
    permissionsLoadedRef.current = permissionsLoaded;

    const goalsRequestIdRef = useRef(0);
    const loadGoals = useCallback(({ showSpinner = true } = {}) => {
        goalsRequestIdRef.current += 1;
        const requestId = goalsRequestIdRef.current;
        if (showSpinner) {
            setGoalsLoading(true);
        }
        fetchGoals()
            .then(value => {
                if (requestId !== goalsRequestIdRef.current) {
                    return;
                }
                setGoals(value || []);
                setLoadError(false);
            })
            .catch(error => {
                if (requestId !== goalsRequestIdRef.current) {
                    return;
                }
                console.error('Failed to load goals', error);
                if (showSpinner) {
                    setLoadError(true);
                }
            })
            .finally(() => {
                if (requestId === goalsRequestIdRef.current) {
                    setGoalsLoading(false);
                }
            });
        return () => {
            goalsRequestIdRef.current += 1;
        };
    }, []);

    const applyDeepLink = useCallback(() => {
        const deepLink = parseShelfDeepLink();
        if (!deepLink) {
            setShelfState(null);
            return;
        }
        if (!permissionsLoadedRef.current) {
            return;
        }
        if (readOnlyRef.current) {
            clearShelfDeepLink();
            setShelfState(null);
            return;
        }
        setShelfState(deepLink);
    }, []);

    useEffect(() => {
        trackGoalsEvent('Goals: Summary: Page View');
        const cancelGoals = loadGoals();
        fetchCostCurrency()
            .then(setCurrencySymbol)
            .catch(error => console.error('Failed to load cost currency', error))
            .finally(() => setCurrencyLoading(false));

        window.addEventListener('hashchange', applyDeepLink);
        return () => {
            window.removeEventListener('hashchange', applyDeepLink);
            cancelGoals();
        };
    }, [loadGoals, applyDeepLink]);

    useEffect(() => {
        if (!permissionsLoaded) {
            return;
        }
        if (readOnly) {
            clearShelfDeepLink();
            setShelfState(null);
            return;
        }
        const deepLink = parseShelfDeepLink();
        if (deepLink) {
            setShelfState(deepLink);
        }
    }, [permissionsLoaded, readOnly]);

    const loading = goalsLoading || currencyLoading;
    const visibleGoals = useMemo(() => sortGoalsByStatus(filterGoalsByName(goals, searchText)), [goals, searchText]);
    const showSearch = goals.length > SEARCH_VISIBILITY_THRESHOLD;

    const openCreateShelf = () => {
        trackGoalsEvent('Create New Goal Button');
        setShelfState({ mode: SHELF_MODES.CREATE, goalId: null });
    };

    const openEditShelf = goal => {
        trackGoalsEvent('Edit Goal Button', { goal_id: goal.public_id });
        setShelfState({ mode: SHELF_MODES.EDIT, goalId: goal.public_id });
    };

    const openDuplicateShelf = goal => {
        setShelfState({ mode: SHELF_MODES.DUPLICATE, goalId: goal.public_id });
    };

    const closeShelf = () => {
        clearShelfDeepLink();
        setShelfState(null);
    };

    const handleShelfSaved = action => {
        clearShelfDeepLink();
        setShelfState(null);
        toaster.success(translate(`${TRANSLATION_KEY}.${SAVE_ACTION_TOASTS[action]}`), TOAST_OPTIONS);
        invalidateGoalsListCache();
        loadGoals({ showSpinner: false });
    };

    const handleDeleteConfirmed = () => {
        setDeleting(true);
        deleteGoal(goalToDelete.public_id)
            .then(goalName => {
                trackGoalsEvent('Goal Deleted', { goal_id: goalToDelete.public_id, goal_name: goalName });
                setGoalToDelete(null);
                toaster.success(translate(`${TRANSLATION_KEY}.GOAL_DELETED_TOAST`), TOAST_OPTIONS);
                invalidateGoalsListCache();
                loadGoals({ showSpinner: false });
            })
            .catch(error => {
                console.error('Failed to delete goal', error);
                setGoalToDelete(null);
                toaster.error(translate(`${TRANSLATION_KEY}.DELETE_ERROR`), TOAST_OPTIONS);
            })
            .finally(() => setDeleting(false));
    };

    return (
        <PageWrapper className={css.page}>
            <PageHeader text="STATIC.PAGE_HEADERS.GOAL_MANAGEMENT" subText={{ faqLink: FAQ_LINK }} />
            <div className={css.toolbar}>
                {showSearch ? (
                    <TextField
                        clearable
                        value={searchText}
                        debounce={200}
                        onChange={setSearchText}
                        containerClass={css.searchInput}
                        placeholder={`${TRANSLATION_KEY}.SEARCH_PLACEHOLDER`}
                    />
                ) : (
                    <span />
                )}
                <PermissionsWrapper write>
                    <Button type="primary" text="STATIC.BUTTONS.NEW_GOAL" onClick={openCreateShelf} />
                </PermissionsWrapper>
            </div>

            {loading ? (
                <div className={css.goalsList}>
                    {Array.from({ length: SKELETON_CARDS_COUNT }, (_, index) => (
                        <GoalCardSkeleton key={index} />
                    ))}
                </div>
            ) : (
                <>
                    {loadError && <div className={css.notification}>{translate(`${TRANSLATION_KEY}.LOAD_ERROR`)}</div>}

                    {!loadError && !goals.length && (
                        <EmptyState
                            icon="startAdding"
                            header={`${TRANSLATION_KEY}.NO_GOALS_TEXT`}
                            containerClass={css.emptyState}
                        >
                            <PermissionsWrapper write>
                                <div className={css.emptyStateAction}>
                                    <Button type="primary" text="STATIC.BUTTONS.NEW_GOAL" onClick={openCreateShelf} />
                                </div>
                            </PermissionsWrapper>
                        </EmptyState>
                    )}

                    {!loadError && !!goals.length && !visibleGoals.length && (
                        <div className={css.noSearchResults}>
                            {translate(`${TRANSLATION_KEY}.NO_SEARCH_RESULTS`)}
                        </div>
                    )}

                    {!loadError && !!visibleGoals.length && (
                        <div className={css.goalsList}>
                            {visibleGoals.map(goal => (
                                <GoalCard
                                    key={goal.public_id}
                                    goal={goal}
                                    currencySymbol={currencySymbol}
                                    onEdit={openEditShelf}
                                    onDuplicate={openDuplicateShelf}
                                    onDelete={setGoalToDelete}
                                    translate={translate}
                                />
                            ))}
                        </div>
                    )}
                </>
            )}

            <GoalShelf
                open={!!shelfState}
                mode={shelfState ? shelfState.mode : SHELF_MODES.CREATE}
                goalId={shelfState ? shelfState.goalId : null}
                currencySymbol={currencySymbol}
                onClose={closeShelf}
                onSaved={handleShelfSaved}
                translate={translate}
            />

            <GeneralPopup
                open={!!goalToDelete}
                type={PopupTypes.DELETE}
                title={`${TRANSLATION_KEY}.DELETE_MODAL.TITLE`}
                text={`${TRANSLATION_KEY}.DELETE_MODAL.MESSAGE`}
                acceptText={`${TRANSLATION_KEY}.DELETE_MODAL.ACCEPT_BUTTON_TEXT`}
                rejectText={`${TRANSLATION_KEY}.DELETE_MODAL.REJECT_BUTTON_TEXT`}
                onAccept={handleDeleteConfirmed}
                onReject={() => setGoalToDelete(null)}
                showSpinner={deleting}
            />
        </PageWrapper>
    );
};

Goals.propTypes = {
    translate: PropTypes.func,
};

export default withLocalize(Goals);
